import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRoxboxPlayerContext } from '@/contexts/RoxboxPlayerContext';
import { useAuth } from '@/components/AuthProvider';
import { useBooks } from '@/hooks/use-books';
import { useAudiobooks, type Audiobook, type AudiobookPage } from '@/hooks/use-audiobooks';
import { useAllAudiobookSessions } from '@/hooks/use-audiobook-tracking';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { prewarmColorExtraction } from '@/hooks/use-color-extraction';
import { useAppFlow } from '@/contexts/AppFlowContext';
import { NowPlaying } from '@/components/roxbox/NowPlaying';
import { MobileTrackHome } from '@/components/roxbox/MobileTrackHome';
import { AudiobookAlbumView } from '@/components/roxbox/AudiobookAlbumView';
import { DesktopPlayerBar } from '@/components/roxbox/DesktopPlayerBar';
import { SubscriptionTierSelector } from '@/components/subscription/SubscriptionTierSelector';
import { AuthModal } from '@/components/AuthModal';
import { GlassCompatibleBackground } from '@/components/ui/glass-compatible-background';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Play, Music, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RoxboxTrack } from '@/contexts/RoxboxPlayerContext';

export default function Roxbox() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const player = useRoxboxPlayerContext();
  const { user } = useAuth();
  const { profileSelectionComplete } = useAppFlow();
  const { allBooks } = useBooks();
  const { data: audiobooks = [], isLoading: isAudiobooksLoading } = useAudiobooks(allBooks);
  const { data: audiobookSessions = [] } = useAllAudiobookSessions();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const isSubscribed = subscriptionStatus?.subscribed || false;
  const hasHandledShareRef = useRef(false);

  // Create a map of book_id to session for quick lookup
  const sessionsByBookId = audiobookSessions.reduce((acc, session) => {
    acc[session.book_id] = session;
    return acc;
  }, {} as Record<number, typeof audiobookSessions[0]>);

  const {
    tracks,
    isLoading,
    isNowPlayingOpen,
    setNowPlayingOpen,
    currentAudiobookPage,
    currentAudiobook,
    setAudiobookPage,
    setPaywallHandler,
  } = player;

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<Audiobook | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const prevUserRef = useRef(user);

  // Reset share handler when user logs in and there's a pending share
  useEffect(() => {
    const pendingShare = sessionStorage.getItem('jukebox-pending-share');
    if (user && !prevUserRef.current && pendingShare) {
      // User just logged in and there's a pending share - reset the handler
      hasHandledShareRef.current = false;
    }
    prevUserRef.current = user;
  }, [user]);

  // Register paywall handler with player context
  const handleOpenPaywall = useCallback(() => {
    setShowPaywall(true);
  }, []);

  useEffect(() => {
    setPaywallHandler(handleOpenPaywall);
    return () => setPaywallHandler(null);
  }, [setPaywallHandler, handleOpenPaywall]);

  // Handle shared track/audiobook from URL params OR stored pending share
  useEffect(() => {
    if (hasHandledShareRef.current || isLoading || isAudiobooksLoading) return;

    // Use context value as primary check, with sessionStorage as fallback
    const isProfileReady =
      profileSelectionComplete || sessionStorage.getItem('profile-selection-shown') === 'true';

    // Check URL params first, then fallback to stored pending share
    let trackId = searchParams.get('track');
    let audiobookId = searchParams.get('audiobook');
    let pageNum = searchParams.get('page');

    // If no URL params, check for pending share/intention from before auth/profile selection
    const pendingShare = sessionStorage.getItem('jukebox-pending-share');
    let pendingAutoPlay: boolean | undefined;
    let pendingSource: 'share' | 'intent' | undefined;

    if (!trackId && !audiobookId && pendingShare) {
      try {
        const parsed = JSON.parse(pendingShare);
        trackId = parsed.trackId || null;
        audiobookId = parsed.audiobookId || null;
        pageNum = parsed.pageNum || null;
        pendingAutoPlay = typeof parsed.autoPlay === 'boolean' ? parsed.autoPlay : undefined;
        pendingSource = parsed.source;
      } catch {
        // ignore
      }
    }

    // If user is signed in but still hasn't selected a profile, DO NOT start playback yet.
    // Keep the intent in sessionStorage and let profile selection complete first.
    if (user && !isProfileReady && (trackId || audiobookId || pendingShare)) {
      // Ensure redirect after profile selection comes back to Jukebox.
      sessionStorage.setItem('post-auth-redirect', '/jukebox');

      // If we have URL params, persist them for after profile selection (since navigation will drop query).
      if (trackId || audiobookId) {
        sessionStorage.setItem(
          'jukebox-pending-share',
          JSON.stringify({
            trackId: trackId || undefined,
            audiobookId: audiobookId || undefined,
            pageNum: pageNum || undefined,
            autoPlay: pendingAutoPlay ?? false,
            source: pendingSource ?? 'share'
          })
        );
      }
      return;
    }

    // At this point, either user is logged out (so we store intent + prompt login),
    // or user is fully ready (signed in + profile selected) and we can open the player.

    if (trackId && tracks.length > 0) {
      const sharedTrack = tracks.find((t) => t.id === trackId);
      if (!sharedTrack) return;

      if (user) {
        hasHandledShareRef.current = true;

        // Clear URL params if present (we've consumed them)
        if (searchParams.get('track')) {
          setSearchParams({}, { replace: true });
        }
        // Clear stored pending share if it was used
        if (pendingShare) sessionStorage.removeItem('jukebox-pending-share');

        const autoPlay = typeof pendingAutoPlay === 'boolean' ? pendingAutoPlay : false;
        player.setTrack(sharedTrack, autoPlay);
        setNowPlayingOpen(true);
      } else {
        // Store for after auth + profile selection
        sessionStorage.setItem(
          'jukebox-pending-share',
          JSON.stringify({ trackId, autoPlay: false, source: 'share' })
        );
        sessionStorage.setItem('post-auth-redirect', '/jukebox');
        setShowAuthModal(true);
      }

      return;
    }

    if (audiobookId && audiobooks.length > 0) {
      const sharedAudiobook = audiobooks.find((a) => a.id === Number(audiobookId));
      if (!sharedAudiobook) return;

      const targetPageNum = pageNum ? Number(pageNum) : 1;
      const targetPage =
        sharedAudiobook.pages.find((p) => p.pageNumber === targetPageNum) ||
        sharedAudiobook.pages[0];
      if (!targetPage) return;

      if (user) {
        hasHandledShareRef.current = true;

        if (searchParams.get('audiobook')) {
          setSearchParams({}, { replace: true });
        }
        if (pendingShare) sessionStorage.removeItem('jukebox-pending-share');

        const autoPlay = typeof pendingAutoPlay === 'boolean' ? pendingAutoPlay : false;
        setAudiobookPage(targetPage, sharedAudiobook, autoPlay);
        setNowPlayingOpen(true);
      } else {
        sessionStorage.setItem(
          'jukebox-pending-share',
          JSON.stringify({
            audiobookId: String(sharedAudiobook.id),
            pageNum: String(targetPageNum),
            autoPlay: false,
            source: 'share'
          })
        );
        sessionStorage.setItem('post-auth-redirect', '/jukebox');
        setSelectedAlbum(sharedAudiobook);
      }
    }
  }, [
    searchParams,
    tracks,
    audiobooks,
    isLoading,
    isAudiobooksLoading,
    user,
    profileSelectionComplete,
    player,
    setAudiobookPage,
    setNowPlayingOpen,
    setSearchParams,
  ]);

  // Handler that checks auth before playing track
  const handleSelectTrack = (track: RoxboxTrack) => {
    if (!user) {
      // Remember the user's intent so after login + profile selection we can return here.
      sessionStorage.setItem('jukebox-pending-share', JSON.stringify({
        trackId: track.id,
        autoPlay: true,
        source: 'intent'
      }));
      sessionStorage.setItem('post-auth-redirect', '/jukebox');
      navigate('/letsbegin');
      return;
    }

    // Start playback immediately, but pre-warm the color cache before opening Now Playing
    // so iOS Safari's glass/status bar samples the *real* extracted color on first open.
    player.setTrack(track);

    const coverUrl = track.cover_image_url;
    Promise.race([
      prewarmColorExtraction(coverUrl),
      new Promise((r) => setTimeout(r, 120)),
    ]).finally(() => {
      setNowPlayingOpen(true);
    });
  };

  // Handler for opening an audiobook "album"
  const handleOpenAudiobook = (audiobook: Audiobook) => {
    setSelectedAlbum(audiobook);
  };

  // Handler for selecting a page (track) within an audiobook
  const handleSelectPage = (page: AudiobookPage, audiobook: Audiobook) => {
    if (!user) {
      // Remember intent so the user returns to this exact audiobook page after auth/profile.
      sessionStorage.setItem('jukebox-pending-share', JSON.stringify({
        audiobookId: String(audiobook.id),
        pageNum: String(page.pageNumber),
        autoPlay: true,
        source: 'intent'
      }));
      sessionStorage.setItem('post-auth-redirect', '/jukebox');
      navigate('/letsbegin');
      return;
    }
    setAudiobookPage(page, audiobook);
    setNowPlayingOpen(true);
  };


  if (isLoading || isAudiobooksLoading) {
    return (
      <>
        {/* iOS 26 Safari glass-compatible background for loading state */}
        <GlassCompatibleBackground topColor="#000000" bottomColor="#000000" />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-white/60">Loading...</div>
        </div>
      </>
    );
  }

  // Mobile: Show full-screen Now Playing when playing
  if (isMobile && isNowPlayingOpen && (player.currentTrack || currentAudiobookPage)) {
    return (
      <>
        <NowPlaying
          track={player.currentTrack}
          isPlaying={player.isPlaying}
          currentTime={player.currentTime}
          duration={player.duration}
          isLooping={player.isLooping}
          onTogglePlay={player.togglePlay}
          onPrevious={player.previousTrack}
          onNext={player.nextTrack}
          onSeek={player.seek}
          onToggleLoop={player.toggleLoop}
          onClose={() => setNowPlayingOpen(false)}
        />
        <SubscriptionTierSelector
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
        />
      </>
    );
  }

  // Show Album View when an audiobook is selected
  if (selectedAlbum) {
    return (
      <>
        <AudiobookAlbumView
          key={selectedAlbum.id}
          audiobook={selectedAlbum}
          currentPage={currentAudiobookPage}
          isPlaying={player.isPlaying}
          isSubscribed={isSubscribed}
          listenedPage={sessionsByBookId[selectedAlbum.id]?.current_page || 0}
          onBack={() => setSelectedAlbum(null)}
          onSelectPage={handleSelectPage}
          onOpenPaywall={handleOpenPaywall}
        />
        <SubscriptionTierSelector
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
        />
      </>
    );
  }

  // Mobile: Just MobileTrackHome
  if (isMobile) {
    return (
      <>
        <GlassCompatibleBackground topColor="#000000" bottomColor="#000000" />
        <MobileTrackHome
          tracks={tracks}
          audiobooks={audiobooks}
          currentTrack={player.currentTrack}
          currentAudiobookPage={currentAudiobookPage}
          selectedAudiobookId={selectedAlbum?.id || null}
          isPlaying={player.isPlaying}
          onSelectTrack={handleSelectTrack}
          onOpenAudiobook={handleOpenAudiobook}
          onTogglePlay={player.togglePlay}
          onOpenNowPlaying={() => setNowPlayingOpen(true)}
          onShowAuthModal={() => setShowAuthModal(true)}
          onShowPaywall={() => setShowPaywall(true)}
        />
        <SubscriptionTierSelector
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
        />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    );
  }

  // Desktop: Sidebar + Main Content + Bottom Player Bar
  const hasActiveTrack = !!(player.currentTrack || currentAudiobookPage);
  
  // Get liked tracks for sidebar
  const likedTracks = tracks.filter(t => {
    // We'll show all tracks in trending for now, liked state comes from MobileTrackHome
    return true;
  });
  
  // Sort audiobooks by review count for "Top Audiobooks"
  const topAudiobooks = [...audiobooks].sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0)).slice(0, 5);
  
  return (
    <>
      <GlassCompatibleBackground topColor="#000000" bottomColor="#000000" />
      <div className={cn("min-h-screen flex pt-16", hasActiveTrack && "pb-[90px]")}>
        {/* Desktop Sidebar - Slim navigation */}
        <aside className="w-56 shrink-0 border-r border-white/10 bg-black/20 backdrop-blur-sm flex flex-col">
          <div className="p-5 border-b border-white/10">
            <h1 className="text-white font-bold text-xl tracking-tight">
              Jukebox
            </h1>
          </div>
          
          <ScrollArea className="flex-1">
            <nav className="p-3 space-y-1">
              {/* Top Audiobooks */}
              <div className="mb-4">
                <h3 className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-2 px-3">
                  Top Audiobooks
                </h3>
                {topAudiobooks.map((audiobook) => {
                  const isCurrentBook = currentAudiobook?.id === audiobook.id;
                  return (
                    <button
                      key={audiobook.id}
                      onClick={() => handleOpenAudiobook(audiobook)}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-left",
                        "hover:bg-white/5",
                        isCurrentBook && "bg-white/10"
                      )}
                    >
                      <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                        <img src={audiobook.coverUrl} alt={audiobook.title} className="w-full h-full object-cover" />
                      </div>
                      <span className={cn(
                        "text-sm truncate",
                        isCurrentBook ? "text-primary font-medium" : "text-white/80"
                      )}>
                        {audiobook.title}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Trending Songs */}
              <div className="mb-4">
                <h3 className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-2 px-3">
                  Trending Songs
                </h3>
                {tracks.slice(0, 5).map((track) => {
                  const isCurrentTrack = player.currentTrack?.id === track.id;
                  return (
                    <button
                      key={track.id}
                      onClick={() => handleSelectTrack(track)}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-left",
                        "hover:bg-white/5",
                        isCurrentTrack && "bg-white/10"
                      )}
                    >
                      <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                        <img src={track.cover_image_url} alt={track.name} className="w-full h-full object-cover" />
                      </div>
                      <span className={cn(
                        "text-sm truncate",
                        isCurrentTrack ? "text-primary font-medium" : "text-white/80"
                      )}>
                        {track.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Liked Songs placeholder - user needs to be logged in */}
              {user && (
                <div>
                  <h3 className="text-white/40 text-[10px] font-semibold uppercase tracking-wider mb-2 px-3">
                    Liked Songs
                  </h3>
                  <p className="text-white/30 text-xs px-3">Songs you love</p>
                </div>
              )}
            </nav>
          </ScrollArea>
        </aside>

        {/* Main Content - Desktop Spotify-style layout */}
        <main className="flex-1 overflow-y-auto p-8">
          <h2 className="text-white font-bold text-2xl mb-6">Good evening</h2>
          
          {/* Hero Row - 2 featured cards */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {/* Featured Track */}
            {tracks[0] && (
              <button
                onClick={() => handleSelectTrack(tracks[0])}
                className="relative aspect-square rounded-xl overflow-hidden group"
              >
                <img 
                  src={tracks[0].cover_image_url} 
                  alt={tracks[0].name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">Featured Track</span>
                  <p className="text-white font-bold text-xl mt-1 truncate">{tracks[0].name}</p>
                  <p className="text-white/60 text-sm truncate">{tracks[0].artist || 'Unknown Artist'}</p>
                </div>
                {player.currentTrack?.id === tracks[0].id && player.isPlaying && (
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <div className="flex items-end gap-[2px] h-4">
                      <span className="w-[3px] bg-white rounded-full animate-pulse" style={{ height: '50%' }} />
                      <span className="w-[3px] bg-white rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.15s' }} />
                      <span className="w-[3px] bg-white rounded-full animate-pulse" style={{ height: '70%', animationDelay: '0.3s' }} />
                    </div>
                  </div>
                )}
              </button>
            )}
            
            {/* Top Audiobook */}
            {topAudiobooks[0] && (
              <button
                onClick={() => handleOpenAudiobook(topAudiobooks[0])}
                className="relative aspect-square rounded-xl overflow-hidden group"
              >
                <img 
                  src={topAudiobooks[0].coverUrl} 
                  alt={topAudiobooks[0].title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider">Top Audiobook</span>
                  <p className="text-white font-bold text-xl mt-1 truncate">{topAudiobooks[0].title}</p>
                  <p className="text-white/60 text-sm truncate">{topAudiobooks[0].totalPages} pages</p>
                </div>
              </button>
            )}
          </div>
          
          {/* All Tracks - 2 column grid */}
          <div className="mb-8">
            <h3 className="text-white/60 font-semibold text-xs uppercase tracking-wider mb-4">
              All Tracks
            </h3>
            <div className="grid grid-cols-2 gap-2 bg-white/5 rounded-xl p-3">
              {tracks.map((track) => {
                const isCurrentTrack = player.currentTrack?.id === track.id;
                return (
                  <button
                    key={track.id}
                    onClick={() => handleSelectTrack(track)}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg transition-colors",
                      isCurrentTrack ? "bg-white/10" : "hover:bg-white/5"
                    )}
                  >
                    <div className="relative w-11 h-11 shrink-0 rounded-md overflow-hidden">
                      <img src={track.cover_image_url} alt={track.name} className="w-full h-full object-cover" />
                      {isCurrentTrack && player.isPlaying && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="flex items-end gap-[2px] h-3">
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '50%' }} />
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '70%', animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className={cn(
                        "font-medium text-sm truncate",
                        isCurrentTrack ? "text-primary" : "text-white"
                      )}>
                        {track.name}
                      </p>
                      <p className="text-white/40 text-xs truncate">{track.artist || 'Unknown'}</p>
                    </div>
                  </button>
                );
              })}
              {tracks.length === 0 && (
                <p className="text-white/30 text-sm text-center py-8 col-span-2">No tracks yet</p>
              )}
            </div>
          </div>

          {/* Audiobooks - 2 column grid, max 10 items (5 rows) */}
          <div>
            <h3 className="text-white/60 font-semibold text-xs uppercase tracking-wider mb-4">
              Audiobooks
            </h3>
            <div className="grid grid-cols-2 gap-2 bg-white/5 rounded-xl p-3">
              {audiobooks.slice(0, 10).map((audiobook) => {
                const isCurrentBook = currentAudiobook?.id === audiobook.id;
                const session = sessionsByBookId[audiobook.id];
                const progressPercent = session 
                  ? Math.round((session.current_page / audiobook.totalPages) * 100) 
                  : 0;
                
                return (
                  <button
                    key={audiobook.id}
                    onClick={() => handleOpenAudiobook(audiobook)}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg transition-colors",
                      isCurrentBook ? "bg-white/10" : "hover:bg-white/5"
                    )}
                  >
                    <div className="w-11 h-11 shrink-0 rounded-md overflow-hidden">
                      <img src={audiobook.coverUrl} alt={audiobook.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className={cn(
                        "font-medium text-sm truncate",
                        isCurrentBook ? "text-primary" : "text-white"
                      )}>
                        {audiobook.title}
                      </p>
                      <p className="text-white/40 text-xs truncate">{audiobook.totalPages} pages</p>
                    </div>
                    {session && progressPercent > 0 && (
                      <span className="text-[10px] font-medium bg-primary/20 text-primary px-1.5 py-0.5 rounded shrink-0">
                        {progressPercent}%
                      </span>
                    )}
                  </button>
                );
              })}
              {audiobooks.length === 0 && (
                <p className="text-white/30 text-sm text-center py-8 col-span-2">No audiobooks yet</p>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Desktop Player Bar - Fixed bottom */}
      {hasActiveTrack && (
        <DesktopPlayerBar
          track={player.currentTrack}
          currentAudiobookPage={currentAudiobookPage}
          isPlaying={player.isPlaying}
          currentTime={player.currentTime}
          duration={player.duration}
          volume={player.volume}
          isLooping={player.isLooping}
          canSkipNext={player.canSkipNext}
          onTogglePlay={player.togglePlay}
          onPrevious={player.previousTrack}
          onNext={player.nextTrack}
          onSeek={player.seek}
          onVolumeChange={player.setVolume}
          onToggleLoop={player.toggleLoop}
          onPaywallHit={handleOpenPaywall}
        />
      )}

      <SubscriptionTierSelector
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
